import { useEffect, useState } from "react";

import toast from "react-hot-toast";

import { getTenant } from "../../../utils/tenantManager";

import { useSearchParams } from "react-router-dom";

import {
  createServiceCallApi,
  deleteServiceCallApi,
  getServiceCallByIdApi,
  getServiceCallsApi,
  updateServiceCallApi,
} from "../../../services/operation/servicecall/serviceCallService";

export const useServiceCallViewModel = () => {
  const tenantId = getTenant();

  const [serviceCalls, setServiceCalls] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const [totalCount, setTotalCount] = useState(0);

  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(Number(searchParams.get("page") ?? "1"));

  const [search, setSearch] = useState(searchParams.get("search") ?? "");

  const [stateFilter, setStateFilter] = useState<number | null>(
    searchParams.get("state") ? Number(searchParams.get("state")) : null,
  );

  const [unAssignedTechnician, setUnAssignedTechnician] = useState(
    searchParams.get("unassigned") === "true",
  );

  // ================= LOAD =================
  const loadServiceCalls = async () => {
    try {
      setLoading(true);

      const res = await getServiceCallsApi(
        page,
        10,
        search,
        tenantId!,

        stateFilter,
        unAssignedTechnician,
      );

      setServiceCalls(res?.data?.data || []);

      setTotalCount(res?.data?.totalCount || 0);
    } catch {
      toast.error("Failed to load service calls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServiceCalls();
  }, [page, search, stateFilter, unAssignedTechnician]);

  // ================= CREATE =================
  const createServiceCall = async (data: any) => {
    try {
      const res = await createServiceCallApi(data, tenantId!);

      if (res?.success) {
        toast.success("Service call created");

        loadServiceCalls();

        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Create failed");

      return false;
    }
  };

  // ================= GET ONE =================
  const getServiceCallById = async (id: string) => {
    try {
      const res = await getServiceCallByIdApi(id, tenantId!);

      return res?.data;
    } catch {
      toast.error("Failed to load service call");
    }
  };

  // ================= UPDATE =================
  const updateServiceCall = async (data: any) => {
    try {
      // ================= VALIDATE TECHNICIAN ROWS =================
      const invalidTechnician = data?.serviceCallTechnicians?.find((x: any) => {
        const hasData =
          x?.hoursWorked ||
          x?.arrival ||
          x?.depature ||
          x?.spareParts ||
          x?.qty;

        return hasData && !x?.dateWorked;
      });

      if (invalidTechnician) {
        toast.error("Technician date worked is required");

        return false;
      }

      const res = await updateServiceCallApi(data, tenantId!);

      if (res?.success) {
        toast.success("Service call updated");

        loadServiceCalls();

        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");

      return false;
    }
  };

  // ================= DELETE =================
  const deleteServiceCall = async (id: string) => {
    try {
      const res = await deleteServiceCallApi(id, tenantId!);

      if (res?.success) {
        loadServiceCalls();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  return {
    // CRUD
    getServiceCallById,

    createServiceCall,

    updateServiceCall,

    deleteServiceCall,

    // DATA
    serviceCalls,

    loading,

    totalCount,

    // PAGINATION
    page,
    setPage,

    // SEARCH
    search,
    setSearch,

    // FILTERS
    stateFilter,
    setStateFilter,

    unAssignedTechnician,
    setUnAssignedTechnician,

    // LOAD
    loadServiceCalls,
  };
};
